import log from '@/lib/logger';
import {useEffect, useMemo, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {z} from 'zod';
import {zodResolver} from '@hookform/resolvers/zod';
import {useMembersTranslation, useCommonTranslation} from '@/i18n/hooks';
import {Dialog, DialogActions, DialogBody, DialogTitle} from '@/components/tailwind/dialog';
import {Button} from '@/components/tailwind/button';
import {Input} from '@/components/tailwind/input';
import {Select} from '@/components/tailwind/select';
import {Field, Label} from '@/components/tailwind/fieldset';
import {Checkbox, CheckboxField} from '@/components/tailwind/checkbox';
import {Text} from '@/components/tailwind/text';
import ValidationAlert from '@/components/validation-alert';
import {useAuth} from '@/context/use-auth';
import {
    SpecialAssignmentService,
    BookingMemberItem,
    SpecialAssignmentBookingDetail,
    getBookingDetail,
    getBookingMembers,
    updateSpecialAssignmentBooking,
} from '@/services/special-assignment-services';

interface SpecialAssignmentEditDialogProps {
    bookingId: string;
    services: SpecialAssignmentService[];
    onClose: () => void;
    onSuccess: () => void;
    onDelete: () => void;
}

function calculatePoints(service: SpecialAssignmentService, startTime?: string, endTime?: string): number | null {
    if (service.isFixedPoints) {
        return service.pointsPerUnit;
    }
    if (!startTime || !endTime) return null;
    const [startH, startM] = startTime.split(':').map(Number);
    const [endH, endM] = endTime.split(':').map(Number);
    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;
    const durationMinutes = endMinutes - startMinutes;
    if (durationMinutes <= 0) return null;
    return Math.ceil(durationMinutes / 60) * service.pointsPerUnit;
}

const editSchema = z.object({
    serviceId: z.string().min(1, 'specialAssignments.bookingDialog.errors.serviceRequired'),
    activityDate: z.string().min(1, 'specialAssignments.bookingDialog.errors.dateRequired'),
    startTime: z.string().min(1, 'specialAssignments.bookingDialog.errors.startTimeRequired'),
    endTime: z.string().min(1, 'specialAssignments.bookingDialog.errors.endTimeRequired'),
}).superRefine((data, ctx) => {
    if (data.startTime && data.endTime && data.endTime <= data.startTime) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'specialAssignments.bookingDialog.errors.endBeforeStart',
            path: ['endTime'],
        });
    }
});

type EditFormData = z.infer<typeof editSchema>;

export default function SpecialAssignmentEditDialog({
    bookingId,
    services,
    onClose,
    onSuccess,
    onDelete,
}: SpecialAssignmentEditDialogProps) {
    const {t} = useMembersTranslation();
    const {t: tCommon} = useCommonTranslation();
    const {token} = useAuth();

    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [bookingDetail, setBookingDetail] = useState<SpecialAssignmentBookingDetail | null>(null);
    const [members, setMembers] = useState<BookingMemberItem[]>([]);
    const [memberIdsToRemove, setMemberIdsToRemove] = useState<Set<string>>(new Set());

    const {
        register,
        handleSubmit,
        control,
        watch,
        setError,
        reset,
        formState: {errors},
    } = useForm<EditFormData>({
        resolver: zodResolver(editSchema),
    });

    useEffect(() => {
        if (!token || !bookingId) return;

        async function loadData() {
            setIsLoading(true);
            try {
                const [detail, memberList] = await Promise.all([
                    getBookingDetail(token, bookingId),
                    getBookingMembers(token, bookingId),
                ]);
                setBookingDetail(detail);
                setMembers(memberList);
                reset({
                    serviceId: detail.serviceId,
                    activityDate: detail.activityDate,
                    startTime: detail.startTime ?? '',
                    endTime: detail.endTime ?? '',
                });
            } catch (error) {
                log.error('Error loading booking detail:', error);
            } finally {
                setIsLoading(false);
            }
        }

        loadData();
    }, [token, bookingId, reset]);

    const watchedServiceId = watch('serviceId');
    const watchedStartTime = watch('startTime');
    const watchedEndTime = watch('endTime');

    const selectedService = services.find(s => s.id === watchedServiceId);
    const newPoints = selectedService ? calculatePoints(selectedService, watchedStartTime, watchedEndTime) : null;

    const remainingMemberCount = members.length - memberIdsToRemove.size;
    const canSubmit = remainingMemberCount >= 1;

    const oldPoints = useMemo(() => bookingDetail?.pointsPerMember ?? 0, [bookingDetail?.pointsPerMember]);

    function toggleMemberRemoval(memberId: string) {
        setMemberIdsToRemove(prev => {
            const next = new Set(prev);
            if (next.has(memberId)) {
                next.delete(memberId);
            } else {
                next.add(memberId);
            }
            return next;
        });
    }

    async function onSubmit(data: EditFormData) {
        if (!canSubmit) return;

        setIsSubmitting(true);
        try {
            await updateSpecialAssignmentBooking(token, bookingId, {
                serviceId: data.serviceId,
                activityDate: data.activityDate,
                startTime: data.startTime,
                endTime: data.endTime,
                memberIdsToRemove: Array.from(memberIdsToRemove),
            });
            onSuccess();
        } catch {
            setError('root', {message: t('specialAssignments.toast.editError.message')});
            setIsSubmitting(false);
        }
    }

    return (
        <Dialog size="3xl" open onClose={onClose}>
            <DialogTitle>{t('specialAssignments.editDialog.title')}</DialogTitle>

            <DialogBody>
                {isLoading ? (
                    <p className="py-4 text-sm text-gray-500 dark:text-gray-400">
                        {t('specialAssignments.detailDialog.loading')}
                    </p>
                ) : (
                    <form id="edit-booking-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                        {errors.root && (
                            <ValidationAlert message={errors.root.message || tCommon('serverError.unknown')}/>
                        )}

                        <Field>
                            <Label>{t('specialAssignments.bookingDialog.fields.service.label')}</Label>
                            <Controller
                                name="serviceId"
                                control={control}
                                render={({field}) => (
                                    <Select
                                        {...field}
                                        invalid={Boolean(errors.serviceId)}
                                        data-testid="edit-service-select"
                                    >
                                        <option value="">
                                            {t('specialAssignments.bookingDialog.fields.service.placeholder')}
                                        </option>
                                        {services.map(service => (
                                            <option key={service.id} value={service.id}>
                                                {service.name}
                                            </option>
                                        ))}
                                    </Select>
                                )}
                            />
                            {errors.serviceId && (
                                <ValidationAlert message={t(errors.serviceId.message || '')}/>
                            )}
                        </Field>

                        <Field>
                            <Label>{t('specialAssignments.bookingDialog.fields.date.label')}</Label>
                            <Input
                                type="date"
                                {...register('activityDate')}
                                invalid={Boolean(errors.activityDate)}
                                data-testid="edit-date-input"
                            />
                            {errors.activityDate && (
                                <ValidationAlert message={t(errors.activityDate.message || '')}/>
                            )}
                        </Field>

                        <div className="grid grid-cols-2 gap-4">
                            <Field>
                                <Label>{t('specialAssignments.bookingDialog.fields.startTime.label')}</Label>
                                <Input
                                    type="time"
                                    {...register('startTime')}
                                    invalid={Boolean(errors.startTime)}
                                    data-testid="edit-start-time-input"
                                />
                                {errors.startTime && (
                                    <ValidationAlert message={t(errors.startTime.message || '')}/>
                                )}
                            </Field>

                            <Field>
                                <Label>{t('specialAssignments.bookingDialog.fields.endTime.label')}</Label>
                                <Input
                                    type="time"
                                    {...register('endTime')}
                                    invalid={Boolean(errors.endTime)}
                                    data-testid="edit-end-time-input"
                                />
                                {errors.endTime && (
                                    <ValidationAlert message={t(errors.endTime.message || '')}/>
                                )}
                            </Field>
                        </div>

                        {newPoints !== null && (
                            <div
                                className="rounded-md bg-blue-50 dark:bg-blue-950 border-l-4 border-blue-400 dark:border-blue-500 p-3 text-sm text-blue-800 dark:text-blue-200"
                                data-testid="edit-points-preview"
                            >
                                {t('specialAssignments.editDialog.pointsPreview', {
                                    newPoints,
                                    oldPoints,
                                })}
                            </div>
                        )}

                        <div>
                            <Text className="mb-2 font-medium">{t('specialAssignments.editDialog.membersSection')}</Text>
                            {!canSubmit && (
                                <ValidationAlert message={t('specialAssignments.editDialog.errors.atLeastOneMember')}/>
                            )}
                            <div className="space-y-1 mt-2">
                                {members.map(member => (
                                    <CheckboxField key={member.memberId}>
                                        <Checkbox
                                            checked={!memberIdsToRemove.has(member.memberId)}
                                            onChange={() => toggleMemberRemoval(member.memberId)}
                                            data-testid={`edit-member-${member.memberId}`}
                                        />
                                        <Text>
                                            {member.firstName} {member.lastName} (#{member.memberNo})
                                        </Text>
                                    </CheckboxField>
                                ))}
                            </div>
                        </div>
                    </form>
                )}
            </DialogBody>

            <DialogActions>
                <Button
                    type="button"
                    color="red"
                    onClick={onDelete}
                    disabled={isSubmitting || isLoading}
                    data-testid="edit-delete-button"
                >
                    {t('specialAssignments.editDialog.buttons.delete')}
                </Button>
                <div className="flex-1" />
                <Button
                    type="button"
                    plain
                    onClick={onClose}
                    aria-label={tCommon('buttons.cancel.ariaLabel')}
                    data-testid="edit-cancel-button"
                >
                    {t('specialAssignments.editDialog.buttons.cancel')}
                </Button>
                <Button
                    type="submit"
                    form="edit-booking-form"
                    disabled={isSubmitting || !canSubmit}
                    data-testid="edit-confirm-button"
                >
                    {t('specialAssignments.editDialog.buttons.confirm')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
